/**
 * ADR-0216 — Atlas Report detail at /app/workspace/atlas-reports/:id.
 *
 * Amendment 1 (owner, 2026-10-16) shapes this page:
 *   - Definition: what the report is — its pipeline, owners, retention, period
 *     rule, subject and description (A7.15: nothing else).
 *   - Data (A7.15): the step → dataset mapping — one row per step the report
 *     reads, in pipeline order, with the dataset that keeps it each run, how many
 *     versions kept runs pin, when it was last written and when its columns
 *     changed (the earlier-shape dataset as a muted row beneath).
 *   - Versions: the per-tab contract is the version's primary object — every
 *     tab of the workbook, its role, where its numbers come from (the contract's
 *     source, else the steps that feed it — the owner: "EVERYTHING HAS A
 *     SOURCE"), how far it is kept current, and what it is; the tab's
 *     instructions open under it. A test build's coverage says what each feed
 *     wrote. Nothing is ever inferred from a tab having no formulas.
 *   - Runs: when it ran, what kind, how it ended, which version, the data as-of
 *     date and the file — nothing else (§A1.8; coverage is the version's).
 *   - Subscriptions riding the report.
 *
 * Amendment 3: a report's template is a workbook or a deck, so a page is a tab
 * or a slide. A slide's facts read "N shapes · M charts · K tables", and under
 * it every shape that shows text, a chart or a table, with what writes it — a
 * step through its feed (or nothing yet; a frozen slide is copied as it is).
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { runStatusPill } from "./TenantAtlasReports.js";
import { InitiativeLinks } from "./TenantInitiatives.js";

type TabRole = "snapshot" | "merge" | "display" | "rolling" | "frozen";

type ServedFeed = {
  feed_id: string;
  step: string;
  at: string;
  kind: string;
  /** Amendment 5: the column carrying each row's period — a closed period's rows come from the run that held it. */
  period_field: string | null;
};

type ServedTab = {
  sheet: string;
  role: TabRole;
  description: string;
  instructions: string;
  source: string | null;
  feeds: ServedFeed[];
  rolling: { period: string; key_cell: string } | null;
  merge: { key_field: string; key_column: string } | null;
  ai: { cells: string[]; inputs: string[] } | null;
};

type SlideShape = {
  name: string;
  kind: "text" | "chart" | "table";
  shows: string;
};

type SheetFacts = {
  sheet: string;
  has_formulas: boolean;
  values: number;
  charts: number;
  hidden: boolean;
  slide?: { position: number; shapes: number; tables: number; named: SlideShape[] };
};

type FeedCoverage = {
  feed_id: string;
  state: "written" | "empty" | "truncated" | "unfed";
  rows: number;
  range: string | null;
  periods_from_run_data?: string[];
  periods_from_step?: string[];
};

type TabCoverage = {
  sheet: string;
  role: TabRole | null;
  feeds: FeedCoverage[];
  uncovered_values: number;
  has_formulas: boolean;
  typed_in_only: boolean;
  ai_cells_written: number;
  cloned_from?: string;
};

type Coverage = {
  tabs: TabCoverage[];
  active_period: string | null;
  cloned_tabs: string[];
  data_as_of: string | null;
};

type Finding = {
  code: string;
  sheet: string | null;
  feed_id: string | null;
  message: string;
};

type ServedVersion = {
  version_id: string;
  version_no: number;
  status: "draft" | "active" | "retired";
  template: { filename: string; bytes: number; hash: string } | null;
  format: "xlsx" | "pptx";
  tabs: ServedTab[];
  tab_count: number;
  feed_count: number;
  findings_count: number;
  findings: Finding[];
  /** Amendment 6: one line per (step, code), the feeds it covers listed. */
  findings_by_step?: Array<{ step: string | null; sheet: string | null; code: string; message: string; feed_ids: string[] }>;
  /** Amendment 6: steps that have not run yet — a state, checked by the first test. */
  pending_steps?: Array<{ step: string; feed_ids: string[]; text: string }>;
  coverage: Coverage | null;
  sheets: SheetFacts[];
  test_passed_at: string | null;
  approved_by: string | null;
  change_note: string | null;
  /** Amendment 5: the version's period rule in words. */
  period: string | null;
  /** Amendment 4: the template patched in place on this version. */
  patch_count: number;
  last_patched_at: string | null;
  last_patched_by: string | null;
  /** ADR-0219 Amendment 1: where the template came from — by mail (who, when) or in chunks; null when unknown. */
  template_source: { kind: "inbox"; received_from: string; received_at: string; ticket: string } | { kind: "chunks" } | null;
  /** ADR-0219 Amendment 1: the hub's one phrase for it ("received by mail from John Kirn on 27 Oct"); null when unknown. */
  template_origin: string | null;
  created_under: string | null;
  created_at: string;
  activated_at: string | null;
  retired_at: string | null;
};

type ServedRun = {
  run_id: string;
  kind: "test" | "scheduled" | "manual";
  status: string;
  version_no: number | null;
  /** The period the run reported on. */
  period: string | null;
  data_as_of: string | null;
  /** Amendment 7 §A7.11: the pipeline revision the build ran. */
  operation_revision?: number | null;
  file: { bytes: number; hash: string; reclaimed: boolean } | null;
  /** Amendment 5: the snapshots the run kept, by dataset; the closed periods it holds; its tombstone. */
  snapshots: Array<{ dataset: string; version_no: number; kind: "run" | "test" }>;
  holds: string[];
  deleted: { at: string; by: string; reason: string } | null;
  error: string | null;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
};

type SubscriptionRiding = {
  subscription_id: string;
  name: string;
  paused: boolean;
  cadence: string;
  recipient_count: number;
  external_recipient_count: number;
  next_fire_at: string | null;
};

/** One dataset the report's runs write (Amendment 7, A7.15): what it keeps, for which step, and the kept runs' facts. */
type ServedDataset = {
  name: string;
  /** The dataset's id — its page; never shown. */
  pdo_id?: string;
  holds: "table" | "values";
  step: string | null;
  /** The step's id in the pipeline — the link to it; never shown. */
  node_id?: string | null;
  /** The persist writing it is the author's own, not one Atlas inserted. */
  authored?: boolean;
  /** The values dataset: the steps whose values it keeps, in pipeline order. */
  fed_by?: Array<{ step: string; node_id: string }>;
  earlier_shape: boolean;
  columns_changed_at?: string | null;
  /** The versions kept runs pin, the oldest such run, and the latest run's data as-of. */
  versions_kept?: number;
  oldest_kept_run_at?: string | null;
  last_written_at?: string | null;
};

type ServedReport = {
  report_id: string;
  name: string;
  slug: string;
  description: string | null;
  status: "active" | "archived";
  operation: { operation_id: string; name: string };
  /** Amendment 8: the owners' names, in the order they were added. */
  owners: string[];
  /** ADR-0219 §4.1: the open and received inbox tickets of this report, newest first (names only; the code is the agent's and the mail's). */
  inbox?: ServedInboxTicket[];
  retention_days: number;
  /** Amendment 5: the period rule in words, or null. */
  period: string | null;
  /** §A1.4: the workspace's own AI provider is connected — an AI-maintained page needs it. */
  ai_provider_connected: boolean;
  /** Amendment 7: the datasets that keep every run's data, each a snapshot per run for retention_days. */
  datasets?: ServedDataset[];
  created_under: string | null;
  created_at: string;
  updated_at: string;
  versions: ServedVersion[];
  active_version: ServedVersion | null;
  draft_version: ServedVersion | null;
  recent_runs: ServedRun[];
  subscriptions: SubscriptionRiding[];
};

/** ADR-0219 §5.2 — a request Atlas mailed for this report's template, as the hub serves it. */
export type ServedInboxTicket = {
  ticket: string;
  kind: "atlas_report_template";
  report: string;
  report_id: string | null;
  status: "open" | "received" | "consumed" | "expired" | "closed";
  opened_by: string;
  sent_to: string;
  opened_at: string;
  expires_at: string;
  received: { from: string; at: string; filename: string; bytes: number; pages: string[] } | null;
  last_message: { at: string; from: string; outcome: string; reason: string | null } | null;
  consumed: { at: string; by: string; version_no: number } | null;
  next_step: string;
};

/** "27 Oct" — the day and month, in the browser's locale. */
function fmtDayMonth(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * ADR-0219 §3.4 — the one inbox line on the Definition card: a received file waiting for commit,
 * else an open request, else nothing. Names and dates only — no code, no id, no actions.
 */
export function inboxLine(inbox: ServedInboxTicket[] | undefined): string | null {
  const received = (inbox ?? []).find((t) => t.status === "received" && t.received);
  if (received?.received) return `Template received from ${received.received.from} on ${fmtDayMonth(received.received.at)}, awaiting commit.`;
  const open = (inbox ?? []).find((t) => t.status === "open");
  if (open) return `Template requested from ${open.sent_to} on ${fmtDayMonth(open.opened_at)}, open until ${fmtDayMonth(open.expires_at)}.`;
  return null;
}

/**
 * ADR-0219 Amendment 1 §A1.4 — the versions table's template cell: the file, and under it, in the
 * table's muted secondary style, where it came from (the hub's phrase: a person and a day — never
 * the ticket code, never an id). Nothing under the filename when the hub does not know.
 */
export function TemplateCell(props: { version: Pick<ServedVersion, "version_no" | "template" | "template_origin">; base: string }): JSX.Element {
  const v = props.version;
  if (!v.template) return <span className="muted">—</span>;
  return (
    <>
      <a href={`${props.base}/versions/${v.version_no}/template`}>
        {v.template.filename} ({fmtBytes(v.template.bytes)})
      </a>
      {v.template_origin ? <div className="muted">{v.template_origin}</div> : null}
    </>
  );
}

/** One row of the contract table: a tab the template has, the contract describes, or both. */
type TabRow = {
  sheet: string;
  tab: ServedTab | null;
  facts: SheetFacts | null;
  coverage: TabCoverage | null;
  clones: string[];
};

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function versionPill(status: ServedVersion["status"]): JSX.Element {
  if (status === "active") return <span className="pill pillOk">active</span>;
  if (status === "draft") return <span className="pill pillWarn">draft</span>;
  return <span className="pill">retired</span>;
}

const ROLE_LABEL: Record<TabRole, string> = {
  snapshot: "Snapshot",
  merge: "Merge",
  display: "Display",
  rolling: "Rolling",
  frozen: "Frozen",
};

const ROLE_MEANING: Record<TabRole, string> = {
  snapshot: "Rewritten from its feeds on every run.",
  merge: "Rows are matched by key: the rows a run brings are updated in place, the rest stay.",
  display: "Never written; its formulas and charts read the pages Atlas fills.",
  rolling: "One page per period: the current one updates, earlier ones are frozen, a new one is created when the period turns.",
  frozen: "Copied as it is into every report; nothing writes it at run time.",
};

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The step names that keep a tab current: its feeds' steps, plus AI when it opted in. */
function fedBy(tab: ServedTab | null): string {
  if (!tab) return "—";
  const steps = [...new Set(tab.feeds.map((f) => f.step))];
  if (tab.ai) steps.push("AI");
  return steps.length > 0 ? steps.join(", ") : "—";
}

/** Where a tab's numbers come from: the contract's own words, else the steps that feed it. */
function sourceOf(tab: ServedTab | null): string {
  if (!tab) return "—";
  return tab.source || fedBy(tab);
}

/**
 * How far a tab is kept current, as one short phrase: what the last test's feeds
 * wrote, or the role's own rule. Never inferred from the absence of formulas.
 */
function coveragePhrase(row: TabRow, tested: boolean, aiConnected: boolean): { text: string; warn: boolean; muted: boolean } {
  const base = rolePhrase(row, tested);
  // §A1.4: an AI-maintained page says whether the model can write it, and what it wrote at the last test.
  if (!row.tab?.ai || row.tab.role === "frozen") return base;
  if (!aiConnected) return { text: "AI-maintained · no provider connected", warn: true, muted: false };
  const n = row.coverage?.ai_cells_written;
  const ai = tested && n !== undefined ? `AI-maintained · ${plural(n, "cell", "cells")} written` : "AI-maintained · not tested";
  return { text: base.text === "No feed writes it" || base.text === "Empty" ? ai : `${base.text} · ${ai}`, warn: base.warn, muted: false };
}

function rolePhrase(row: TabRow, tested: boolean): { text: string; warn: boolean; muted: boolean } {
  const role = row.tab?.role ?? null;
  if (!row.tab)
    return {
      text: row.facts ? "Not described" : "Not in the template",
      warn: true,
      muted: false,
    };
  if (!row.facts) return { text: "Not in the template", warn: true, muted: false };
  if (role === "frozen") return { text: "Frozen — copied as it is", warn: false, muted: true };
  const cov = row.coverage;
  if (role === "snapshot" || role === "merge") {
    if (!tested || !cov)
      return {
        text: "Not tested under this contract",
        warn: false,
        muted: true,
      };
    const rows = cov.feeds.reduce((n, f) => n + f.rows, 0);
    const unfed = cov.feeds.filter((f) => f.state === "unfed" || f.state === "empty").length;
    if (unfed > 0)
      return {
        text: `${plural(unfed, "feed", "feeds")} brought no rows`,
        warn: true,
        muted: false,
      };
    if (row.facts.slide) {
      const written = cov.feeds.length;
      return { text: `${plural(written, "shape", "shapes")} written`, warn: false, muted: false };
    }
    return {
      text: `${plural(rows, "row", "rows")} written`,
      warn: false,
      muted: false,
    };
  }
  // display or rolling
  if (row.facts.has_formulas)
    return {
      text: "Formulas, recalculated when opened",
      warn: false,
      muted: false,
    };
  if (row.facts.values > 0) return { text: "No feed writes it", warn: true, muted: false };
  return { text: "Empty", warn: false, muted: true };
}

export function TenantAtlasReportDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";

  const [report, setReport] = useState<ServedReport | null>(null);
  const [initiatives, setInitiatives] = useState<Array<{ id: string; ref?: string; name: string }>>([]);
  const [runs, setRuns] = useState<ServedRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versionNo, setVersionNo] = useState<number | null>(null);
  const [openTabs, setOpenTabs] = useState<Set<string>>(new Set());
  const [section, setSection] = useState<"pages" | "runs" | "subscriptions" | "data">("pages");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, runList] = await Promise.all([
        apiFetch<{ report: ServedReport; initiatives?: Array<{ id: string; ref?: string; name: string }> }>(`/api/tenant/atlas-reports/${encodeURIComponent(id)}`),
        apiFetch<{ runs: ServedRun[] }>(`/api/tenant/atlas-reports/${encodeURIComponent(id)}/runs?limit=50`),
      ]);
      setReport(detail.report);
      setInitiatives(detail.initiatives ?? []);
      setRuns(runList.runs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // The contract shown: the chosen version, else the active one, else the draft, else the newest.
  const version = useMemo<ServedVersion | null>(() => {
    if (!report) return null;
    return (
      report.versions.find((v) => v.version_no === versionNo) ?? report.active_version ?? report.draft_version ?? report.versions[0] ?? null
    );
  }, [report, versionNo]);

  const rows = useMemo<TabRow[]>(() => {
    if (!version) return [];
    const tabBySheet = new Map(version.tabs.map((t) => [t.sheet, t]));
    const covBySheet = new Map((version.coverage?.tabs ?? []).map((t) => [t.sheet, t]));
    const clonesOf = (sheet: string) => (version.coverage?.tabs ?? []).filter((t) => t.cloned_from === sheet).map((t) => t.sheet);
    const out: TabRow[] = version.sheets.map((facts) => ({
      sheet: facts.sheet,
      tab: tabBySheet.get(facts.sheet) ?? null,
      facts,
      coverage: covBySheet.get(facts.sheet) ?? null,
      clones: clonesOf(facts.sheet),
    }));
    const inTemplate = new Set(version.sheets.map((s) => s.sheet));
    for (const t of version.tabs) {
      if (!inTemplate.has(t.sheet))
        out.push({
          sheet: t.sheet,
          tab: t,
          facts: null,
          coverage: null,
          clones: [],
        });
    }
    return out;
  }, [version]);

  function toggleTab(sheet: string): void {
    setOpenTabs((prev) => {
      const next = new Set(prev);
      if (next.has(sheet)) next.delete(sheet);
      else next.add(sheet);
      return next;
    });
  }

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  if (!report) return null;

  const base = `/api/tenant/atlas-reports/${encodeURIComponent(report.report_id)}`;
  const tested = !!version?.coverage;

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <Link className="breadcrumbLink" to="/app/workspace/atlas-reports">
            Atlas Reports
          </Link>
          <span className="breadcrumbSep">/</span>
          <span>{report.name}</span>
        </span>
      }
    >
      {/* minmax(0, 1fr): a wide table scrolls inside its card, never the page. */}
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}>
        <DefinitionCard report={report} initiatives={initiatives} />

        {/* Versions */}
        <div className="card adminList">
          <h3>Versions</h3>
          {report.versions.length === 0 ? (
            <div className="adminListEmpty">No versions yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Template</th>
                    <th>Pages described</th>
                    <th>Test passed</th>
                    <th>Approved by</th>
                    <th>Patched</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {report.versions.map((v) => (
                    <tr key={v.version_id}>
                      <td>
                        {version && v.version_no === version.version_no ? (
                          <span className="adminListName">v{v.version_no}</span>
                        ) : (
                          <button
                            type="button"
                            className="adminListAction"
                            onClick={() => {
                              setVersionNo(v.version_no);
                              setSection("pages");
                            }}
                          >
                            v{v.version_no}
                          </button>
                        )}
                      </td>
                      <td>{versionPill(v.status)}</td>
                      <td>
                        <TemplateCell version={v} base={base} />
                      </td>
                      <td>{v.sheets.length > 0 ? `${v.tab_count} of ${v.sheets.length}` : v.tab_count}</td>
                      <td className="muted">{fmtTime(v.test_passed_at)}</td>
                      <td>{v.approved_by ?? <span className="muted">—</span>}</td>
                      {/* Amendment 4: the template replaced in place — no new version, no approval. */}
                      <td className="muted" style={{ whiteSpace: "nowrap" }}>
                        {v.patch_count > 0 ? `${v.patch_count}×, last ${fmtDate(v.last_patched_at)} by ${v.last_patched_by ?? "—"}` : "—"}
                      </td>
                      {/* The note fills the remaining width on one line; the full text is on hover. */}
                      <td className="muted" style={{ width: "100%", maxWidth: 0 }}>
                        <div
                          style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          title={v.change_note ?? undefined}
                        >
                          {v.change_note ?? "—"}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pages · Runs · Subscriptions */}
        <div>
          <div className="tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={section === "pages"}
              className={`tab${section === "pages" ? " tabActive" : ""}`}
              onClick={() => setSection("pages")}
            >
              Pages
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={section === "runs"}
              className={`tab${section === "runs" ? " tabActive" : ""}`}
              onClick={() => setSection("runs")}
            >
              Runs
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={section === "subscriptions"}
              className={`tab${section === "subscriptions" ? " tabActive" : ""}`}
              onClick={() => setSection("subscriptions")}
            >
              Subscriptions
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={section === "data"}
              className={`tab${section === "data" ? " tabActive" : ""}`}
              onClick={() => setSection("data")}
            >
              Data
            </button>
          </div>

          {/* The contract of the version shown */}
          {section === "pages" && version ? (
            <div className="card adminList">
              <h3>
                Pages — v{version.version_no} ({version.status})
              </h3>
              {(version.pending_steps ?? []).length > 0 ? (
                <div className="help" style={{ marginBottom: 10 }}>
                  <strong>Not run yet — the first test runs these steps and checks their columns:</strong>
                  <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                    {(version.pending_steps ?? []).map((p) => (
                      <li key={p.step}>{p.text}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {version.findings.length > 0 ? (
                <div className="help" style={{ marginBottom: 10 }}>
                  <strong>{plural(version.findings.length, "finding", "findings")} — a test is refused until they are resolved:</strong>
                  <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                    {(
                      version.findings_by_step ??
                      version.findings.map((f) => ({
                        step: null,
                        sheet: f.sheet,
                        code: f.code,
                        message: f.message,
                        feed_ids: f.feed_id ? [f.feed_id] : [],
                      }))
                    ).map((g, i) => (
                      <li key={i}>
                        {(g.step ?? g.sheet) ? `${g.step ?? g.sheet}: ` : ""}
                        {g.message}
                        {g.feed_ids.length > 1 ? (
                          <details style={{ display: "inline-block", marginLeft: 6 }}>
                            <summary className="muted">{g.feed_ids.length} feeds</summary>
                            <span className="muted">{g.feed_ids.join(", ")}</span>
                          </details>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {!tested && version.tabs.length > 0 ? (
                <div className="help" style={{ marginBottom: 10 }}>
                  Not tested under this contract yet — a test build shows what each feed wrote.
                </div>
              ) : null}
              {rows.length === 0 ? (
                <div className="adminListEmpty">No template yet — the pages are described once a workbook or a deck is uploaded.</div>
              ) : (
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th>Role</th>
                        <th>Source</th>
                        <th>Coverage</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <TabRows
                          key={row.sheet}
                          row={row}
                          tested={tested}
                          aiConnected={report.ai_provider_connected}
                          open={openTabs.has(row.sheet)}
                          onToggle={() => toggleTab(row.sheet)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {/* Runs */}
          {section === "runs" ? (
            <div className="card adminList">
              {runs.length === 0 ? (
                <div className="adminListEmpty">No runs yet.</div>
              ) : (
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Started</th>
                        <th>Kind</th>
                        <th>Status</th>
                        <th>Version</th>
                        <th>Pipeline</th>
                        <th>Period</th>
                        <th>Data as of</th>
                        <th>File</th>
                        <th>Snapshot</th>
                        <th>Why it failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((r) => (
                        <tr key={r.run_id} style={r.deleted ? { opacity: 0.6 } : undefined}>
                          <td className="muted">{fmtTime(r.started_at ?? r.queued_at)}</td>
                          <td>{r.kind}</td>
                          <td>{runStatusPill(r.status)}</td>
                          <td>{r.version_no != null ? `v${r.version_no}` : "—"}</td>
                          <td className="muted">{r.operation_revision != null ? `rev ${r.operation_revision}` : "—"}</td>
                          <td>{r.period ?? "—"}</td>
                          <td className="muted">{fmtDate(r.data_as_of)}</td>
                          <td>
                            {r.deleted ? (
                              <span className="muted">
                                Deleted {fmtDate(r.deleted.at)} by {r.deleted.by} — {r.deleted.reason}
                              </span>
                            ) : r.file ? (
                              r.file.reclaimed ? (
                                <span className="muted">reclaimed</span>
                              ) : (
                                <a href={`${base}/runs/${encodeURIComponent(r.run_id)}/file`}>{fmtBytes(r.file.bytes)}</a>
                              )
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td>
                            {r.deleted ? (
                              <span className="muted">removed</span>
                            ) : r.snapshots.length === 0 ? (
                              <span className="muted">—</span>
                            ) : (
                              <>
                                {/* Amendment 7: a run keeps one snapshot per table step plus its values — a list folds away. */}
                                <details>
                                  <summary className={r.kind === "test" ? "muted" : undefined}>
                                    {r.snapshots.length === 1 ? "1 dataset" : `${r.snapshots.length} datasets`}
                                    {r.kind === "test" ? " (test — replaced by the next test, removed on approval)" : ""}
                                  </summary>
                                  {r.snapshots.map((x) => (
                                    <div key={`${x.dataset}-${x.version_no}`} className="muted">
                                      {x.dataset} v{x.version_no}
                                    </div>
                                  ))}
                                </details>
                                {r.holds.length > 0 ? (
                                  <span className="pill pillOk">
                                    {r.file?.reclaimed ? "kept: " : ""}holds {r.holds.join(", ")}
                                  </span>
                                ) : null}
                              </>
                            )}
                          </td>
                          <td className="muted" style={{ whiteSpace: "pre-wrap" }}>
                            {r.status === "failed" && r.error ? r.error : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {/* Subscriptions riding the report */}
          {section === "subscriptions" ? (
            <div className="card adminList">
              {report.subscriptions.length === 0 ? (
                <div className="adminListEmpty">No subscription delivers this report yet.</div>
              ) : (
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Cadence</th>
                        <th>Recipients</th>
                        <th>Status</th>
                        <th>Next fire</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.subscriptions.map((s) => (
                        <tr key={s.subscription_id}>
                          <td>
                            <Link className="adminListName" to={`/app/schedule/subscriptions/${encodeURIComponent(s.subscription_id)}`}>
                              {s.name}
                            </Link>
                          </td>
                          <td className="muted">{s.cadence}</td>
                          <td>{s.recipient_count}</td>
                          <td>{s.paused ? <span className="pill pillWarn">Paused</span> : <span className="pill pillOk">Active</span>}</td>
                          <td className="muted">{fmtTime(s.next_fire_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {/* A7.15: the step → dataset mapping */}
          {section === "data" ? <DataTab report={report} /> : null}
        </div>
      </div>
    </PageLayout>
  );
}

/** What the report is (A7.15): its pipeline, owners, retention, period rule, subject and description — nothing about its data. */
export function DefinitionCard(props: { report: ServedReport; initiatives?: Array<{ id: string; ref?: string; name: string }> }): JSX.Element {
  const { report } = props;
  return (
    <div className="card">
      <h3>Definition</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10 }}>
        <div className="field">
          <span className="label">Operation</span>
          <Link to={`/app/build/operations/${encodeURIComponent(report.operation.operation_id)}`}>{report.operation.name}</Link>
        </div>
        <div className="field">
          <span className="label">Owners</span>
          <div>{report.owners.join(", ")}</div>
        </div>
        <div className="field">
          <span className="label">Keeps each run for</span>
          <div>{report.retention_days} days — its file and its data</div>
        </div>
        <div className="field">
          <span className="label">Periods</span>
          <div>{report.period ?? "No period rule — every run shows what the pipeline returns"}</div>
        </div>
        <div className="field">
          <span className="label">Created under</span>
          <div>{report.created_under ?? "—"}</div>
        </div>
        {/* ADR-0220 §3.5: the sixth field — the initiatives this report serves. */}
        <div className="field">
          <span className="label">Initiatives</span>
          <div>
            <InitiativeLinks initiatives={props.initiatives} />
          </div>
        </div>
      </div>
      {report.description ? (
        <div className="muted" style={{ marginTop: 10 }}>
          {report.description}
        </div>
      ) : null}
      {inboxLine(report.inbox) ? (
        <div className="help" style={{ marginTop: 10 }}>
          {inboxLine(report.inbox)}
        </div>
      ) : null}
      {report.status === "archived" ? (
        <div className="help" style={{ marginTop: 10 }}>
          This report is archived. Nothing is delivered; its runs' files and data stay until their retention passes.
        </div>
      ) : null}
    </div>
  );
}

export const DATA_EMPTY_TEXT =
  "Nothing yet — once the contract is set, Atlas adds a stored dataset in the pipeline after every step the report reads, and each run keeps a version of it.";

/** "N · oldest <date>": the versions kept runs pin and the oldest such run; "none yet" before the first delivery. */
function versionsKept(d: ServedDataset | undefined): string {
  const n = d?.versions_kept;
  if (n === undefined) return "—";
  if (n === 0) return "none yet";
  return `${n} · oldest ${fmtDate(d?.oldest_kept_run_at ?? null)}`;
}

/** One step the report reads, with the dataset that keeps it; a scalar step's dataset is the report's values dataset. */
type DataRow = { step: string; node_id: string | null; dataset: ServedDataset | undefined; scalar: boolean; earlier: ServedDataset[] };

/**
 * The step → dataset mapping (A7.15): one row per step the report reads, in the
 * order the hub serves them (pipeline order), the values dataset first with the
 * steps that feed it; an earlier-shape dataset is a muted row under its step.
 */
export function DataTab(props: { report: ServedReport }): JSX.Element {
  const { report } = props;
  const datasets = report.datasets ?? [];
  const opPath = `/app/build/operations/${encodeURIComponent(report.operation.operation_id)}`;
  const stepLink = (step: string, nodeId: string | null) =>
    nodeId ? <Link to={`${opPath}?node=${encodeURIComponent(nodeId)}`}>{step}</Link> : <span>{step}</span>;
  const datasetLink = (d: ServedDataset, label = d.name) =>
    d.pdo_id ? <Link to={`/app/build/pdos/${encodeURIComponent(d.pdo_id)}`}>{label}</Link> : <span>{label}</span>;
  const values = datasets.find((d) => d.holds === "values" && !d.earlier_shape);
  const sameStep = (a: ServedDataset, step: string, nodeId: string | null) =>
    a.node_id && nodeId ? a.node_id === nodeId : a.step === step;
  const rows: DataRow[] = datasets
    .filter((d) => d.holds === "table" && !d.earlier_shape)
    .map((d) => ({
      step: d.step ?? "—",
      node_id: d.node_id ?? null,
      dataset: d,
      scalar: false,
      earlier: datasets.filter((e) => e.earlier_shape && e.holds === "table" && sameStep(e, d.step ?? "—", d.node_id ?? null)),
    }));
  for (const f of values?.fed_by ?? []) {
    if (rows.some((r) => (r.node_id && f.node_id ? r.node_id === f.node_id : r.step === f.step))) continue;
    rows.push({ step: f.step, node_id: f.node_id, dataset: values, scalar: true, earlier: [] });
  }
  // An earlier shape whose step the report no longer reads: a row of its own.
  const orphans = datasets.filter((e) => e.earlier_shape && !rows.some((r) => r.earlier.includes(e)));
  const earlierRow = (e: ServedDataset) => (
    <tr key={`earlier-${e.name}`} className="muted">
      <td>{e.holds === "values" ? "every value the pages read" : ""}</td>
      <td>{datasetLink(e)}</td>
      <td>{e.holds === "values" ? "its values" : "the rows it returned"}</td>
      <td>{versionsKept(e)}</td>
      <td>{fmtDate(e.last_written_at ?? null)}</td>
      <td>
        {e.columns_changed_at ? `changed ${fmtDate(e.columns_changed_at)} · ` : ""}no longer written — kept until its runs pass retention
      </td>
    </tr>
  );
  return (
    <div className="card adminList">
      {datasets.length === 0 ? (
        <div className="adminListEmpty">{DATA_EMPTY_TEXT}</div>
      ) : (
        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Step</th>
                <th>Dataset</th>
                <th>Holds</th>
                <th>Versions kept</th>
                <th>Last written</th>
                <th>Columns</th>
              </tr>
            </thead>
            <tbody>
              {values ? (
                <tr key="values">
                  <td>
                    every value the pages read
                    {(values.fed_by ?? []).length > 0 ? (
                      <span className="muted"> — from {(values.fed_by ?? []).map((f) => f.step).join(", ")}</span>
                    ) : null}
                  </td>
                  <td>{datasetLink(values)}</td>
                  <td>its values</td>
                  <td>{versionsKept(values)}</td>
                  <td className="muted">{fmtDate(values.last_written_at ?? null)}</td>
                  <td className="muted">—</td>
                </tr>
              ) : null}
              {rows.map((r) => (
                <Fragment key={r.node_id ?? r.step}>
                  <tr>
                    <td>{stepLink(r.step, r.node_id)}</td>
                    <td>
                      {r.scalar ? (r.dataset ? datasetLink(r.dataset, "values") : "values") : r.dataset ? datasetLink(r.dataset) : "—"}
                      {r.dataset?.authored ? (
                        <>
                          {" "}
                          <span className="pill">authored</span>
                        </>
                      ) : null}
                    </td>
                    <td>{r.scalar ? "its values" : "the rows it returns"}</td>
                    <td>{versionsKept(r.dataset)}</td>
                    <td className="muted">{fmtDate(r.dataset?.last_written_at ?? null)}</td>
                    <td className={r.dataset?.columns_changed_at ? undefined : "muted"}>
                      {r.dataset?.columns_changed_at ? `changed ${fmtDate(r.dataset.columns_changed_at)}` : "—"}
                    </td>
                  </tr>
                  {r.earlier.map(earlierRow)}
                </Fragment>
              ))}
              {orphans.map(earlierRow)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabRows(props: { row: TabRow; tested: boolean; aiConnected: boolean; open: boolean; onToggle: () => void }): JSX.Element {
  const { row, tested, aiConnected, open, onToggle } = props;
  const phrase = coveragePhrase(row, tested, aiConnected);
  return (
    <>
      <tr>
        <td>
          <button
            type="button"
            className="disclosureToggle disclosureToggleInline disclosureToggleName"
            aria-expanded={open}
            onClick={onToggle}
          >
            {row.sheet}
          </button>
        </td>
        <td>{row.tab ? ROLE_LABEL[row.tab.role] : <span className="muted">—</span>}</td>
        <td>{sourceOf(row.tab)}</td>
        <td className={phrase.warn ? "textWarn" : phrase.muted ? "muted" : undefined}>{phrase.text}</td>
        <td>{row.tab?.description || <span className="muted">—</span>}</td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={5} style={{ background: "var(--panel, #f8fafc)" }}>
            <TabDetail row={row} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function TabDetail(props: { row: TabRow }): JSX.Element {
  const { row } = props;
  const { tab, facts, coverage } = row;
  const lines: string[] = [];
  if (tab) {
    lines.push(ROLE_MEANING[tab.role]);
    if (tab.source && tab.feeds.length > 0) lines.push(`Fed by: ${fedBy(tab)}.`);
    // On a slide the shapes table below says what each feed writes.
    for (const f of facts?.slide ? [] : tab.feeds) {
      const freeze = f.period_field ? ` (a closed period's rows, by ${f.period_field}, come from the run that held it)` : "";
      lines.push(`Feed “${f.feed_id}”: “${f.step}” writes at ${f.at}${freeze}.`);
    }
    if (tab.rolling) lines.push(`A new page each ${tab.rolling.period}; the ${tab.rolling.period} is in ${tab.rolling.key_cell}.`);
    if (tab.merge) lines.push(`Rows matched on ${tab.merge.key_field} in column ${tab.merge.key_column}.`);
    if (tab.ai)
      lines.push(`AI writes ${tab.ai.cells.join(", ")}${tab.ai.inputs.length > 0 ? `, reading ${tab.ai.inputs.join(", ")}` : ""}.`);
  }
  if (facts?.slide) {
    const s = facts.slide;
    lines.push(
      `Template: slide ${s.position} · ${plural(s.shapes, "shape", "shapes")} · ${plural(facts.charts, "chart", "charts")} · ${plural(s.tables, "table", "tables")}.`,
    );
  } else if (facts) {
    const bits = [facts.has_formulas ? "has formulas" : "no formulas", `${facts.values} cells with values`];
    if (facts.charts > 0) bits.push(plural(facts.charts, "chart", "charts"));
    if (facts.hidden) bits.push("hidden");
    lines.push(`Template: ${bits.join(" · ")}.`);
  }
  if (coverage) {
    for (const f of coverage.feeds) {
      const shape = facts?.slide?.named.find((x) => x.name === f.range);
      const what =
        shape?.kind === "text" && f.state === "written"
          ? `wrote ${f.range}`
          : f.state === "written"
            ? `${plural(f.rows, "row", "rows")} to ${f.range}`
            : f.state === "truncated"
              ? `${plural(f.rows, "row", "rows")} to ${f.range}, more were cut off`
              : f.state === "empty"
                ? "no rows — the range was cleared"
                : "fed nothing";
      lines.push(`Last test, “${f.feed_id}”: ${what}.`);
      if (f.periods_from_run_data && f.periods_from_run_data.length > 0)
        lines.push(`Kept from earlier runs: ${f.periods_from_run_data.join(", ")}.`);
    }
    if (coverage.uncovered_values > 0 && !coverage.typed_in_only)
      lines.push(`${plural(coverage.uncovered_values, "number", "numbers")} on this page no feed refreshes.`);
  }
  if (row.clones.length > 0) lines.push(`Period pages created in the last test: ${row.clones.join(", ")}.`);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div className="field">
        <span className="label">How it is maintained</span>
        <div style={{ whiteSpace: "pre-wrap" }}>{tab?.instructions || <span className="muted">Not described in the contract.</span>}</div>
      </div>
      {lines.length > 0 ? (
        <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
          {lines.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      ) : null}
      {facts?.slide && facts.slide.named.length > 0 ? <SlideShapes shapes={facts.slide.named} tab={tab} /> : null}
    </div>
  );
}

const SHAPE_KIND_LABEL: Record<SlideShape["kind"], string> = { text: "Text", chart: "Chart", table: "Table" };

/** A slide's shapes that show something, and what writes each: a step through its feed; on a frozen slide, nothing. */
function SlideShapes(props: { shapes: SlideShape[]; tab: ServedTab | null }): JSX.Element {
  const feedOf = new Map((props.tab?.feeds ?? []).map((f) => [f.at, f]));
  const frozen = props.tab?.role === "frozen";
  return (
    <div className="field">
      <span className="label">Shapes</span>
      <table className="table">
        <thead>
          <tr>
            <th>Shape</th>
            <th>Kind</th>
            <th>Shows in the template</th>
            <th>Written by</th>
          </tr>
        </thead>
        <tbody>
          {props.shapes.map((s) => {
            const feed = feedOf.get(s.name);
            return (
              <tr key={s.name}>
                <td style={{ whiteSpace: "nowrap" }}>{s.name}</td>
                <td className="muted">{SHAPE_KIND_LABEL[s.kind]}</td>
                {/* One line, the full text on hover (the page's truncate-with-title rule). */}
                <td style={{ width: "100%", maxWidth: 0 }}>
                  <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={s.shows}>
                    {s.shows}
                  </div>
                </td>
                <td className={feed ? undefined : "muted"} style={{ whiteSpace: "nowrap" }}>
                  {feed ? `“${feed.step}”` : frozen ? "Copied as it is" : "Not fed yet"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
